import React, { useRef } from 'react';
import { Box } from '@ublend-npm/aulaui-core';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Mousewheel } from 'swiper/modules';
import HomeGreeting from '../greeting';
import SpaceCard from './SpaceCard';
import SpaceCardSkeleton from './SpaceCardSkeleton';
import {
  GREETING_CARD_HEIGHT_PX,
  GREETING_CARD_WIDTH_PX,
  SPACE_CARD_GAP_PX,
  SPACE_CARD_HEIGHT_PX,
  SPACE_CARD_SKELETON_SURFACE,
  SPACE_CARD_WIDTH_PX,
} from './spaceWidgetConstants';
import { SpaceWidgetCardData } from './SpaceWidget.types';
import SpaceWidgetError, { SpaceWidgetErrorVariant } from './SpaceWidgetError';
import { useSpaceWidgetCarouselWheel } from './useSpaceWidgetCarouselWheel';

import 'swiper/css';

type SpaceWidgetCarouselProps = {
  spaces: SpaceWidgetCardData[];
  showGreeting?: boolean;
  showErrorCard?: boolean;
  errorVariant?: SpaceWidgetErrorVariant;
  onErrorRetry?: () => void;
  errorRetrying?: boolean;
  trailingSkeletonCount?: number;
  onSwiper: (swiper: SwiperType) => void;
  onSlideChange: (swiper: SwiperType) => void;
  onReachEnd: () => void;
  onTransitionEnd?: (swiper: SwiperType) => void;
};

const spaceCardSlideSx = {
  width: `${SPACE_CARD_WIDTH_PX}px`,
  maxWidth: `${SPACE_CARD_WIDTH_PX}px`,
  flexShrink: 0,
};

const greetingCarouselItemSx = {
  width: `${GREETING_CARD_WIDTH_PX}px`,
  maxWidth: `${GREETING_CARD_WIDTH_PX}px`,
  height: `${GREETING_CARD_HEIGHT_PX}px`,
  minHeight: `${GREETING_CARD_HEIGHT_PX}px`,
  flexShrink: 0,
  '& > section': {
    width: '100%',
    height: '100%',
    minHeight: `${GREETING_CARD_HEIGHT_PX}px`,
  },
};

const SpaceWidgetCarousel = ({
  spaces,
  showGreeting = false,
  showErrorCard = false,
  errorVariant = 'api',
  onErrorRetry,
  errorRetrying = false,
  trailingSkeletonCount = 0,
  onSwiper,
  onSlideChange,
  onReachEnd,
  onTransitionEnd,
}: SpaceWidgetCarouselProps) => {
  const containerRef = useRef<HTMLElement>(null);

  useSpaceWidgetCarouselWheel(containerRef);

  return (
    <Box
      ref={containerRef}
      component="section"
      aria-label="Your spaces"
      aria-busy={trailingSkeletonCount > 0}
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden',
        minHeight: { lg: `${SPACE_CARD_HEIGHT_PX}px` },
        overscrollBehaviorX: 'contain',
        '& .swiper': {
          width: '100%',
          overflow: 'hidden',
          overscrollBehaviorX: 'contain',
        },
        '& .swiper-wrapper': {
          alignItems: 'stretch',
        },
        '& .swiper-slide': {
          width: 'auto',
          flexShrink: 0,
          height: 'auto',
          boxSizing: 'border-box',
        },
      }}
    >
      <Swiper
        modules={[Mousewheel]}
        direction="horizontal"
        slidesPerView="auto"
        spaceBetween={SPACE_CARD_GAP_PX}
        watchOverflow
        watchSlidesProgress
        roundLengths
        resistance
        resistanceRatio={0}
        grabCursor
        mousewheel={{
          forceToAxis: true,
          releaseOnEdges: false,
          sensitivity: 1,
          thresholdDelta: 5,
        }}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        onReachEnd={onReachEnd}
        onTransitionEnd={onTransitionEnd}
      >
        {showGreeting ? (
          <SwiperSlide style={{ width: GREETING_CARD_WIDTH_PX }}>
            <Box
              id="home-greeting-widget"
              role="listitem"
              aria-label="Greeting Widgets"
              sx={greetingCarouselItemSx}
            >
              <HomeGreeting />
            </Box>
          </SwiperSlide>
        ) : null}

        {spaces.map((space) => (
          <SwiperSlide key={space.id} style={{ width: SPACE_CARD_WIDTH_PX }}>
            <Box role="listitem" sx={spaceCardSlideSx}>
              <SpaceCard space={space} tabIndex={0} />
            </Box>
          </SwiperSlide>
        ))}

        {trailingSkeletonCount > 0
          ? Array.from({ length: trailingSkeletonCount }, (_, index) => (
              <SwiperSlide
                key={`space-card-skeleton-${index}`}
                style={{ width: SPACE_CARD_WIDTH_PX }}
              >
                <Box role="listitem" sx={spaceCardSlideSx}>
                  <SpaceCardSkeleton
                    skeletonSurface={SPACE_CARD_SKELETON_SURFACE}
                  />
                </Box>
              </SwiperSlide>
            ))
          : null}

        {showErrorCard && onErrorRetry ? (
          <SwiperSlide style={{ width: SPACE_CARD_WIDTH_PX }}>
            <Box role="listitem" sx={spaceCardSlideSx}>
              <SpaceWidgetError
                variant={errorVariant}
                onRetry={onErrorRetry}
                isRetrying={errorRetrying}
              />
            </Box>
          </SwiperSlide>
        ) : null}
      </Swiper>
    </Box>
  );
};

export default SpaceWidgetCarousel;
